/**
 * The permission each client screen needs — one table for the sidebar and the router (N9).
 *
 * Found 2026-09-29: the sidebar dropped an item the user could not view, but its screen still
 * opened by typing its address, and only Activity checked for itself. The service refuses every
 * read behind these screens on the same permission (`app/api/*`, `require_permission`), so no
 * data leaked — but the screen opened and then showed whatever error each call returned. Now the
 * router asks the same question the sidebar does, from this table, and the answer is one sentence.
 *
 * **Each entry is the permission the service enforces for that screen's reads.** The GSTR-1,
 * GSTR-3B, Prepared vs Filed, Books vs Return, Reports and Download screens were gated on
 * `return.can_view` while every route behind them checks `period.can_view` — `rbac.RETURN` is
 * checked by no route at all. Every role grants the two identically today, so nobody could see the
 * difference; a role that ever split them would have been shown a screen and refused its data, or
 * the reverse. `tests/screen-access.test.ts` holds this table to the service's own checks.
 */

import { CLIENT, DOCUMENT, PERIOD, RECON, type Verb } from './access'

export type ScreenNeeds = readonly [resource: string, verb: Verb]

export const SCREEN_NEEDS = {
  '': [CLIENT, 'can_view'],
  intake: [DOCUMENT, 'can_view'],
  review: [DOCUMENT, 'can_view'],
  returns: [PERIOD, 'can_view'],
  gstr3b: [PERIOD, 'can_view'],
  reconciliation: [RECON, 'can_view'],
  'filed-compare': [PERIOD, 'can_view'],
  'tb-recon': [PERIOD, 'can_view'],
  reports: [PERIOD, 'can_view'],
  download: [PERIOD, 'can_view'],
  // `client.can_view`, deliberately the same permission as the client record itself: this is a
  // list of what happened to that client's work.
  activity: [CLIENT, 'can_view'],
  // The portal session's state reads on `period.can_view` (`GET /filing/{client_id}`) — anyone
  // who can see the client's returns sees whether a session is open. Opening one needs more
  // (`filing.can_update`, the manager's — ND-E12), and the screen asks that separately.
  filing: [PERIOD, 'can_view'],
} as const satisfies Record<string, ScreenNeeds>

export type ClientScreen = keyof typeof SCREEN_NEEDS

/**
 * The surface each gated client screen belongs to — FR-3's gate at the UI (ADR-0008 §Decision-3).
 *
 * A record-only registration type (composition, casual, ISD, non-resident, or any type the
 * service's gate table has never heard of) has no return preparation and no reconciliation. Its
 * screens used to open and show whatever the service refused; now `ScreenGate` reads the client's
 * `surfaces` (served by `ClientOut`, from `gating.surfaces_for`) and says so instead of rendering
 * the screen at all, so no request is made. **The registration types are not named here**: the
 * service's table is the one answer, and this table only says which question each screen asks.
 *
 * Not listed, so open for every type: Client Details (`''`), Upload Data (`intake`), Raw Data
 * (`review`) and Activity — the client's records, which every type keeps. Reconciliation is
 * `recon` (D-150): importing a 2B stays allowed at the API, as a record, but the screen is where
 * it is reconciled.
 */
export const SCREEN_SURFACE = {
  returns: 'returns',
  gstr3b: 'returns',
  reconciliation: 'recon',
  'filed-compare': 'returns',
  'tb-recon': 'returns',
  reports: 'returns',
  download: 'returns',
  filing: 'returns',
} as const satisfies Partial<Record<ClientScreen, Surface>>

/** The gated surfaces, as `ClientRecord.surfaces` names them. */
export type Surface = 'returns' | 'recon'

/** The surface a screen needs, or `null` when every registration type may open it. */
export function surfaceOf(screen: ClientScreen | OrgScreen): Surface | null {
  return (SCREEN_SURFACE as Partial<Record<string, Surface>>)[screen] ?? null
}

/**
 * The org-level screens — beside `/clients`, not inside a client — and the permission each
 * needs, held to the service's check on its main read the same way (`tests/screen-access.test.ts`).
 * Kept apart from `SCREEN_NEEDS`, whose keys are exactly the client screens and are what the
 * client router and the flat-path redirects are built from.
 */
export const ORG_SCREEN_NEEDS = {
  // The calendar reads `GET /periods/obligations`, which checks `period.can_view`.
  calendar: [PERIOD, 'can_view'],
} as const satisfies Record<string, ScreenNeeds>

export type OrgScreen = keyof typeof ORG_SCREEN_NEEDS

/** What a screen — client or org — needs, by its key. */
export function needsOf(screen: ClientScreen | OrgScreen): ScreenNeeds {
  return screen in ORG_SCREEN_NEEDS
    ? ORG_SCREEN_NEEDS[screen as OrgScreen]
    : SCREEN_NEEDS[screen as ClientScreen]
}
